import { inject, Injectable } from '@angular/core';
import { ConfigBaseFilter, ConfigService, GridLayoutComponent, ListLayout, ListLayoutMultipleComponent, ListSortOption, SelectionAction } from 'gung-list';
import { first, forkJoin, mergeMap, Observable, switchMap, toArray } from 'rxjs';
import { ProductExportCardComponent } from '../../components/product-export-card/product-export-card.component';
import { ProductExportTableComponent } from '../../components/product-export-table/product-export-table.component';
import { Product } from '../../models/product';
import { AuthService } from '../auth/auth.service';
import { BaseViewConfigService, ProductViewType } from '../base-view-config/base-view-config.service';
import { ProductExportActionConfigService } from '../product-export-action-config/product-export-action-config.service';
import { ProductExportService } from '../product-export/product-export.service';
import { ProductListConfigService } from '../product-list-config/product-list-config.service';
import { ProductListV2ConfigService } from '../product-list-v2-config/product-list-v2-config.service';
import { ProductSortService } from '../product-sort/product-sort.service';
import { ProductService } from '../products/product.service';
import { PRODUCT_DOCUMENT_TYPE_ID } from '../../modules/pdf-document-builder/models/pdf-document-builder.model';

@Injectable({
  providedIn: 'root'
})
export class ProductExportListConfigService implements ConfigService<Product> {
  protected productService = inject(ProductService);
  protected authService = inject(AuthService);
  protected productExportService = inject(ProductExportService);
  protected productSortService = inject(ProductSortService);
  protected productListConfigService = inject(ProductListConfigService);
  protected productExportActionConfigService = inject(ProductExportActionConfigService);
  protected productListV2ConfigService = inject(ProductListV2ConfigService);
  protected baseViewConfigService = inject(BaseViewConfigService);

  topFilter = this.productListConfigService.topFilter;
  digitalAssets = false;

  getFilters(): ConfigBaseFilter<Product>[] {
    if (this.baseViewConfigService.productViewType === ProductViewType.productV2) {
      return this.productListV2ConfigService.getFilters();
    } else {
      return this.productListConfigService.getFilters();
    }
  }

  getSortOptions(): ListSortOption<Product>[] {
    return this.productListConfigService.getSortOptions();
  }

  getBatchSizes(): number[] {
    return this.productListConfigService.getBatchSizes();
  }

  getLayouts(): ListLayout<Product>[] {
    return [
      {
        getIconClass: () => 'fas fa-th',
        getListItemComponent: () => ProductExportCardComponent,
        getListLayoutComponent: () => GridLayoutComponent,
        getName: () => 'ProductGrid'
      },
      {
        getIconClass: () => 'fas fa-list',
        getListItemComponent: () => ProductExportTableComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'ProductList'
      }
    ];
  }

  getItems(): Observable<Product[]> {
    return this.authService.getCurrentUser().pipe(
      first(),
      switchMap(user => {
        this.productListConfigService.assortmentSubject.next(user.assortment);
        if (this.digitalAssets) {
          return this.productService.getProductsForDigitalAssets(
            user.assortment,
            this.productListConfigService.displayProductsOnVariantLevel
          );
        } else {
          if (this.baseViewConfigService.productViewType === ProductViewType.productV2) {
            return this.productListV2ConfigService.getItems();
          } else {
            return this.productListConfigService.getItems();
          }
        }
      })
    );
  }

  getItemId(item: Product) {
    return this.productListConfigService.getItemId(item);
  }

  getSearchTerms(item: Product) {
    if (this.baseViewConfigService.productViewType === ProductViewType.productV2) {
      return this.productListV2ConfigService.getSearchTerms(item);
    } else {
      return this.productListConfigService.getSearchTerms(item);
    }
  }

  getSelectionActions(): Observable<SelectionAction<Product>[]> {
    return forkJoin([
      this.productExportActionConfigService.getSelectionActions().pipe(first()),
      this.productExportActionConfigService.getDynamicSelectionActions().pipe(first()),
      this.productExportActionConfigService
        .getPdfDocumentBuilderExportActions(PRODUCT_DOCUMENT_TYPE_ID)
        .pipe(first())
    ]).pipe(
      mergeMap(([staticActions, dynamicActions, pdfBuilderActions]) => {
        const res = [];
        res.push(...staticActions);
        res.push(...dynamicActions);
        res.push(...pdfBuilderActions);
        return res;
      }),
      toArray()
    );
  }

  getSelectionMarkingActions(): Observable<SelectionAction<Product>[]> {
    return this.productExportActionConfigService.getSelectionMarkingActions();
  }

  getNothingFoundTranslateTag() {
    if (this.digitalAssets) {
      return 'ADD_FLOW_ADIGITAL_ASSET';
    } else {
      return 'NOTHING_FOUND_CONTACT_GUNG';
    }
  }
}
